import React, { useState, useCallback } from 'react';

import {
  BuilderProps,
  Query,
  Builder,
  BasicConfig,
  JsonGroup,
  Config,
  ImmutableTree,
  Utils as QbUtils,
  Fields,
  AntdConfig,
} from '@react-awesome-query-builder/antd';

import 'antd/dist/antd.css';

import '@react-awesome-query-builder/antd/css/styles.css';

import { SortEnd } from 'react-sortable-hoc';
// eslint-disable-next-line import/no-cycle
import RulesContainer from './rules.container';
import IfRuleSection from './if.rule.section';
import { Input, Rule } from '../../../../store/modules/rules';
import { useActions } from '../../../providers/actions-provider/actions.context';
import { useStore } from '../../../../store';
import WithActionsContainer from './with.actions.container';

type IIfRuleProps = {
  rule: Rule;
  level: number;
  label?: string;
  // eslint-disable-next-line
  onChange: (value: any, rule: Rule, input: Input | null) => void,
  onStatementTypeChange: (type: string, rule: Rule) => void,
  index: number;
  target: string;
};

// eslint-disable-next-line
const createTree = (tree: ImmutableTree | undefined, defaultValue: JsonGroup, config: any) => {
  try {
    return QbUtils.checkTree(
      tree || QbUtils.loadTree(defaultValue),
      config,
    );
  } catch (e) {
    return QbUtils.checkTree(
      QbUtils.loadTree(defaultValue),
      config,
    );
  }
};

// eslint-disable-next-line
const decodeCondition = (condition: any, config: any) => {
  try {
    return QbUtils.loadFromJsonLogic(condition || {}, config);
  } catch (e) {
    // eslint-disable-next-line
    console.log('Cannot decode rule condition', condition, e);
    return QbUtils.loadFromJsonLogic({}, config);
  }
};

const IfRule = ({
  rule,
  level, label = 'IF',
  onChange,
  onStatementTypeChange,
  index,
  target,
}: IIfRuleProps): JSX.Element | null => {
  const {
    rulesFields,
  } = useStore();
  const { ruleActions } = useActions();

  const qbConfig: Config = {
    ...AntdConfig,
    ...BasicConfig,
    fields: rulesFields.data as Fields,
  };

  const logicTree = decodeCondition(rule.condition, qbConfig);
  const emptyInitValue: JsonGroup = { id: QbUtils.uuid(), type: 'group' };

  const [state, setState] = useState({
    tree: createTree(logicTree, emptyInitValue, qbConfig),
    config: qbConfig,
  });

  const renderBuilder = useCallback((props: BuilderProps) => (
    <div className="query-builder-container" style={{ padding: '10px' }}>
      <div className="query-builder">
        {/* eslint-disable-next-line */}
        <Builder {...props} />
      </div>
    </div>
  ), []);
  const onChangeValue = (immutableTree: ImmutableTree, configuration: Config) => {
    // Tip: for better performance you can apply `throttle` - see `examples/demo`
    setState({ tree: immutableTree, config: configuration });
    const logic = QbUtils.jsonLogicFormat(immutableTree, configuration);
    onChange(logic.logic, rule, null);
  };

  const onSortEnd = (sort: SortEnd) => {
    ruleActions.moveRule(sort.oldIndex, sort.newIndex, target, rule);
  };

  const thenRules = (rule.then || []) as Rule[];
  const elseThenRules = (rule.else || []) as Rule[];
  const elseIfs = (rule.elseif || []) as Rule[];

  return (
    <WithActionsContainer
      onDelete={() => ruleActions.deleteRule(rule, target)}
      overStyle={{ backgroundColor: '#E5E0FF' }}
      containerExtraClassNames={['rule-if-container']}
      index={index}
      active={rule.active}
      onActiveChange={() => ruleActions.changeActive(rule, target)}
    >
      <IfRuleSection label={label}>
        <Query
          // eslint-disable-next-line
            {...qbConfig}
          value={state.tree}
          onChange={onChangeValue}
          renderBuilder={renderBuilder}
        />
      </IfRuleSection>
      <IfRuleSection label="THEN">
        <RulesContainer
          rules={thenRules}
          level={level}
          onChange={onChange}
          onStatementTypeChange={onStatementTypeChange}
          onSortEnd={onSortEnd}
          useDragHandle
          target={target}
        />
      </IfRuleSection>

      {elseIfs && elseIfs.length > 0 && (
        <>
          <IfRuleSection label="ELSE IFS">
            {elseIfs.map((ei: Rule, idx: number) => (
              <IfRule
              // eslint-disable-next-line
                key={`level_${level}_${rule.name}_${idx}`}
                rule={ei}
                level={level + 1}
                label="ELSE IF"
                onChange={onChange}
                onStatementTypeChange={onStatementTypeChange}
                index={idx}
                target={target}
              />
            ))}
          </IfRuleSection>
        </>
      )}
      {elseThenRules && elseThenRules.length > 0 && (
        <>
          <IfRuleSection label="ELSE">
            <RulesContainer
              rules={elseThenRules}
              level={level}
              onChange={onChange}
              onStatementTypeChange={onStatementTypeChange}
              onSortEnd={onSortEnd}
              useDragHandle
              target={target}
            />
          </IfRuleSection>
        </>
      )}
    </WithActionsContainer>
  );
};

export default IfRule;
